/* eslint-disable react-hooks/exhaustive-deps */
import { connect } from "react-redux";
import { Form, Checkbox } from 'antd';
import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { StorageManager } from "datastake-daf/src/helpers/StorageManager";
import Button from '../../components/Button';
import useQuery from "../../helpers/useQuery";
import { filterOptions } from "../../helpers/form";
import DefaultForm from "./components/DefaultForm";
import { changeLanguage } from "../../helpers/Language";
import AuthLayout from "../../components/Layout/AuthLayout";
import { actions } from "../../store/reducers/authentication";
import TifForm from "./components/TifForm";
import { createModule } from "../../helpers/user";
import { USER_TYPES } from "./components/config";
import Redirect from "../../components/Redirect";
import TazamaForm from "./components/TazamaForm";
import { noDatastakeApps } from "../../constants/apps";

const mstp = ({ authentication }) => ({ authentication });

export const TermsAggreed = ({ termsAgreed, setTermsAgreed, t, app }) => (
  <Checkbox
    checked={termsAgreed}
    onChange={(e) => setTermsAgreed(e.target.checked)}
  >
    {noDatastakeApps.includes(app) ? (
      <p className="mb-0">
        {t('Agree to the Terms of Use of this application and the Privacy Policy')}
      </p>
    ) : (
      <p className="mb-0">
        {t("Agree with the")}
        {' '}
        <a href={`${process.env.REACT_APP_LANDING_PAGE_URL}terms`} target="_blank" rel="noreferrer">
          {t('Terms of Use')}
        </a>
        {' '}
        {t('and acknowledge the')}
        {' '}
        <a href={`${process.env.REACT_APP_LANDING_PAGE_URL}privacy`} target="_blank" rel="noreferrer">
          {t('Datastake Privacy Policy')}
        </a>
      </p>
    )}
  </Checkbox>
)

const initialValues = {
  mailUpdates: true,
  registrationType: 'organization',
};

const Register = ({
  authentication: {
    authenticated,
    user,
    registerLoading,
    registerErrors: errors,
    registrationData,
    registrationOptions,
  },
  register,
  getRegistrationData,
  getRegistrationOptions,
  app = 'mmt',
}) => {
  const params = useQuery();
  const code = params.get('code');
  const { t } = useTranslation();
  const { language: paramLanguage } = useParams();
  const [countries, setCountries] = useState([]);
  const [positionInSupplyChain, setPositionInSupplyChanin] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [economicSectorOptions, setEconomicSector] = useState([]);
  const [step, setStep] = useState(0);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const goTo = useNavigate();
  const [showInputs, setShowHideInputs] = useState({
    subCategory: false,
    economicSector: false,
    positionInSupplyChain: false,
    name: true,
  });

  const [categoryUnderstood, setCategoryUnderstood] = useState(false);
  const [organisation, setOrganisation] = useState();
  const [form] = Form.useForm();
  const language = StorageManager.get("datastakeLng") || 'en';
  const displayError = (name) => {
    return errors && errors[name] && errors[name].length > 0 && {
      help: errors[name][0],
      validateStatus: 'error',
    }
  }

  useEffect(() => {
    if (typeof paramLanguage === 'string') {
      if (['en', 'fr'].includes(paramLanguage)) {
        changeLanguage(paramLanguage);
      }
    }
    if (params.get('language')) {
      const lng = params.get('language');
      if (['en', 'fr'].includes(lng)) {
        changeLanguage(lng);
      }
    }
  }, []);

  useEffect(() => {
    if (code && !registrationData) {
      getRegistrationData(code);
    }
    if (registrationData) {
      const values = initialValues;
      Object.keys(registrationData).forEach(key => {
        Object.assign(values, { [key]: registrationData[key] })
      });

      Object.keys(registrationData?.otherData || {}).forEach(key => {
        Object.assign(values, { [key]: registrationData.otherData[key] })
      });

      form.setFieldsValue(values);
    }
  }, [code, getRegistrationData, registrationData]);

  useEffect(() => {
    getRegistrationOptions({ language });
  }, [language])

  useEffect(() => {
    if (registrationOptions && Object.keys(registrationOptions).length > 0) {
      setCountries(registrationOptions.countries)
      setCategories(registrationOptions.category)
      setOrganisation(registrationOptions.category);
      setSubCategories(registrationOptions.subCategory)
      setEconomicSector(registrationOptions.economicSector)
      setPositionInSupplyChanin(registrationOptions.positionInSupplyChain)
    }
  }, [registrationOptions])

  const pText = useMemo(() => {
    switch (step) {
      case 0:
        return 'Please introduce yourself';
      case 1:
        return 'Help us guide you to the right modules';
      case 2:
        return 'Set up your password and review the terms and conditions';
      default:
        return '';
    }
  }, [step]);

  const redirectPath = useMemo(() => {
    const confirmUrl = app === 'mmt' ? '/confirm/register' : `/${app}/confirm/register`;

    if (user && user.modules) {
      const allModules = Object.keys(user.modules).filter(m => user.modules[m].status === 'active' || user.modules[m].status === 'approved');

      if (!user.emailVerified || user.approvalStatus === 'pending') {
        if (!user.emailVerified) {
          return `${confirmUrl}?type=notVerified`;
        } else {
          return `${confirmUrl}?type=verified`
        }
      }

      if (app && app !== 'mmt') {
        return `/app/${app}`;
      }

      if (process.env.REACT_APP_APP) {
        return `/app/${process.env.REACT_APP_APP}`;
      }

      if (allModules.length === 1) {
        return `/app/${allModules[0]}`;
      } else if (allModules.length > 1) {
        return `/app`;
      }
    }
    return confirmUrl;
  }, [user, app])

  const onValuesChange = (val, all) => {
    if (all.category && registrationOptions && Object.keys(registrationOptions).length > 0) {
      const options = filterOptions(registrationOptions.subCategory, ['stkCategory|category'], all);
      if (options.length !== subCategories.length || !subCategories.every(s => options.find(o => o.value === s.value))) {
        setSubCategories(options);
      }
      const option = options.find(o => o.value === all.subCategory);
      if (!option) {
        form.setFieldsValue({ subCategory: undefined });
      }
      const changed = {
        ...showInputs,
        subCategory: (all.category && all.category !== ''),
        economicSector: all.category === 'corporation',
        name: all.category !== 'individual',
        positionInSupplyChain: all.category && all.category === 'corporation' && all.economicSector && all.economicSector === 'miningMineralTrade',
      };
      if (!changed.economicSector) {
        form.setFieldsValue({ economicSector: undefined });
      }
      if (!changed.name) {
        form.setFieldsValue({ name: undefined });
      }
      if (!changed.subCategory) {
        form.setFieldsValue({ subCategory: undefined });
      }
      if (!changed.positionInSupplyChain) {
        form.setFieldsValue({ positionInTheMineralSupplyChain: undefined });
      }
      if (Object.keys(showInputs).length !== Object.keys(changed).length || !Object.keys(showInputs).every(k => showInputs[k] === changed[k])) {
        setShowHideInputs({
          ...changed
        });
      }
    }
  }

  const props = {
    onValuesChange, form, initialValues, displayError, t, step, registrationData, organisation,
    setOrganisation, categories, categoryUnderstood, setCategoryUnderstood, showInputs, registrationOptions,
    subCategories, economicSectorOptions, positionInSupplyChain, countries, termsAgreed, setTermsAgreed,
  };

  const renderForm = () => {
    switch (app) {
      case 'tif':
        return <TifForm {...props} code={code} />;
      case 'tazama':
        return <TazamaForm {...props} />;
      default:
        return <DefaultForm {...props} />;
    }
  };

  const getLogInLink = () => {
    if (app === 'mmt') {
      return '/login';
    }

    return `/${app}/login`;
  }

  const getConfirmText = () => {
    switch (app) {
      case 'tazama':
        return "Confirm";
      default:
        return "Create Account";
    }
  };

  return authenticated && redirectPath ? (
    <Redirect to={redirectPath} />
  ) : (
    <AuthLayout step={step} title="Welcome" subTitle={pText} app={app}>
      <>
        {renderForm()}
        <div className="buttons">
          <Button
            className="normal-br"
            size="large"
            disabled={registerLoading ? true : step === 2 ? !termsAgreed : false}
            onClick={() => {
              form.validateFields()
                .then(payload => {
                  if (step === 2) {
                    delete payload.confirmPassword;
                    if (registrationData) {
                      Object.assign(payload, { id: registrationData.id });
                    }
                    payload.language = StorageManager.get("datastakeLng");
                    payload.code = code;
                    if (app === 'tif') {
                      payload.modules = createModule('tif');

                      if (payload.userType === USER_TYPES.MINING_PRODUCER || payload.userType === USER_TYPES.BUYER_MINERALS) {
                        payload.category = 'corporation';
                        payload.economicSector = 'miningMineralTrade';
                      }

                      if (payload.userType === USER_TYPES.MINING_PRODUCER && code) {
                        payload.emailVerified = true;
                      }

                      if (registrationData) {
                        if (registrationData.location) {
                          payload.location = registrationData.location;
                        }

                        if (registrationData.ampParent) {
                          payload.ampParent = registrationData.ampParent;
                        }
                      }
                    } else if (app === 'tazama') {
                      payload.modules = createModule('tazama');
                    } else {
                      if (payload.category === 'corporation' && payload.economicSector === 'miningMineralTrade') {
                        payload.modules = createModule('cadd');
                      } else {
                        payload.modules = createModule('pme');
                      }
                    }

                    const callback = () => goTo(app ? `/${app}/login?approved=true` : '/login?approved=true');

                    if (app === 'tif') {
                      register({ ...registrationData, ...payload, callback });
                    } else {
                      register({ ...payload, callback });
                    }
                  } else {
                    setStep((prev) => prev + 1);
                  }
                })
                .catch((err) => {
                  console.log(err);
                });
            }}
            type="primary"
            block
          >
            {registerLoading ? `${t("Registering your account")} ...` : step !== 2 ? t('Next') : t(getConfirmText())}
          </Button>
          {step === 0 ? (
            <div className="bottom">
              {registrationData ? (
                <p>
                  {t('Your organisation already has an account?')}
                  {' '}
                  <Link to="/login">{t('Log in')}</Link>
                </p>
              ) : (
                <>
                  {app === 'mmt' && (
                    <p>
                      {t('You prefer to explore features without registering?')}
                      {' '}
                      <a href={process.env.REACT_APP_DEMO_BASE_URL}>{t('Try Demo')}</a>
                    </p>
                  )}
                  <p>
                    {t('Your organisation already has an account?')}
                    {' '}
                    <Link to={getLogInLink()}>{t('Log in')}</Link>
                  </p>
                </>
              )}
            </div>
          ) : (
            <Button
              size="large"
              block
              htmlType="submit"
              disabled={registerLoading}
              loading={registerLoading}
              className="normal-br default"
              onClick={() => {
                if (step === 0) {
                  goTo(-1);
                  return;
                }
                setStep((p) => p - 1);
              }}
            >
              {t("Back")}
            </Button>
          )}
        </div>
      </>
    </AuthLayout >
  );
};


const MappedRegister = connect(mstp, actions)(Register);

export const AppRegister = () => {
  const { app: _app } = useParams();
  const app = process.env.REACT_APP_APP ? process.env.REACT_APP_APP : _app;

  // useEffect(() => {
  //   if (StorageManager.get('token')) {
  //     StorageManager.clearOne('token');
  //     window.location.reload();
  //   }
  // }, []);

  if (app === 'tif') {
    return <MappedRegister app="tif" />
  }

  if (app === 'tazama') {
    return <MappedRegister app="tazama" />
  }

  return <MappedRegister />
};

export default MappedRegister;
